import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import ContactSection from '../components/ContactSection';
import { Boxes, CheckCircle2, Shield, FileText, Layers } from 'lucide-react';
import { breadcrumbSchema } from '../utils/structuredData';

const ScanToBimPage = () => {
  const features = [
    'Revit modelling from LOD 200 to LOD 500',
    'Architectural, structural and MEP elements',
    'ISO 19650 and PAS 1192 compliant workflows',
    'Clash detection and coordination reports',
    'IFC export for open BIM collaboration',
    'Quality assurance with deviation analysis'
  ];

  const lodLevels = [
    {
      level: 'LOD 200',
      title: 'Conceptual Design',
      description: 'Generic placeholders with approximate geometry, sizes and locations. Suitable for early-stage feasibility studies and space planning where general spatial understanding is needed but detailed design has not yet begun.',
      typical: 'Walls, floors, roofs as generic elements with approximate dimensions'
    },
    {
      level: 'LOD 300',
      title: 'Detailed Design',
      description: 'Specific geometry with accurate size, shape, location and orientation. Elements are modelled to their true as-built condition. This is the most common LOD for renovation and refurbishment projects where accurate existing conditions are required for design development.',
      typical: 'Walls with accurate thickness, window and door openings, column positions, floor levels'
    },
    {
      level: 'LOD 400',
      title: 'Construction Documentation',
      description: 'Fabrication-level detail including connections, fixings, material specifications and construction sequences. Used where the BIM model will directly inform manufacturing, procurement or on-site construction activities.',
      typical: 'Steel connections, curtain wall mullion profiles, service pipe diameters and routing'
    },
    {
      level: 'LOD 500',
      title: 'As-Built Record',
      description: 'Field-verified model representing the completed building in its actual constructed condition. The model is verified against scan data and serves as the authoritative digital record for facilities management and future works.',
      typical: 'Verified element positions, confirmed specifications, linked asset data'
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: 'Standards Compliance',
      description: 'All models delivered to ISO 19650 and PAS 1192 standards, meeting requirements for public sector and major contractor projects.'
    },
    {
      icon: Layers,
      title: 'Accurate As-Built Data',
      description: 'Models built directly from survey-grade point cloud data, ensuring every element reflects the true condition of the building.'
    },
    {
      icon: FileText,
      title: 'Coordinated Deliverables',
      description: 'Architectural, structural and MEP models coordinated in a single federated environment with clash detection reports.'
    }
  ];

  const faqs = [
    {
      question: 'What is Scan to BIM?',
      answer: 'Scan to BIM is the process of converting 3D laser scan data (point clouds) into an intelligent Building Information Model (BIM). The point cloud provides accurate spatial measurements, and our modellers use this data to create a Revit model that represents the building\'s geometry, structure and services with the level of detail required for your project.'
    },
    {
      question: 'What LOD do I need for my project?',
      answer: 'The appropriate Level of Development depends on your project requirements. LOD 200 is suitable for feasibility studies, LOD 300 for design development and planning, LOD 400 for construction documentation, and LOD 500 for as-built records. Most renovation and refurbishment projects require LOD 300. We can advise on the right level for your specific needs.'
    },
    {
      question: 'How long does Scan to BIM take?',
      answer: 'Timescales depend on the size and complexity of the building and the LOD required. A typical LOD 300 model of a single-storey commercial space can be completed within 5-7 working days after receiving the point cloud data. Larger or more complex projects are quoted individually with agreed milestones.'
    },
    {
      question: 'What file formats do you deliver?',
      answer: 'We deliver native Revit (.rvt) files as standard, along with IFC exports for open BIM collaboration. We can also provide DWG, PDF and NWC formats as required. All models include a detailed model production and delivery table documenting the elements modelled and the LOD achieved.'
    },
    {
      question: 'Do you comply with ISO 19650 and PAS 1192?',
      answer: 'Yes. Our Scan to BIM workflows follow ISO 19650 information management standards and the legacy PAS 1192 framework. We produce BIM Execution Plans, maintain naming conventions, and deliver models with proper classification and parameter structures. This ensures our outputs integrate smoothly into BIM-mandated project environments.'
    },
    {
      question: 'Do I need a separate point cloud survey, or is it included?',
      answer: 'Our Scan to BIM service includes the point cloud survey as part of the package. We handle everything from the initial site scan through to the completed Revit model. If you already have point cloud data from a previous survey, we can work from your existing dataset — just share the files and we will assess their suitability for modelling.'
    }
  ];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Scan to BIM",
    "name": "Scan to BIM Services London",
    "provider": {
      "@type": "Organization",
      "name": "Digital Scanning Group",
      "url": "https://www.digitalscanninggroup.com"
    },
    "areaServed": [
      { "@type": "Place", "name": "London" },
      { "@type": "Place", "name": "United Kingdom" }
    ],
    "description": "Professional Scan to BIM services in London. Revit modelling from point cloud data at LOD 200-500, compliant with ISO 19650 and PAS 1192.",
    "offers": {
      "@type": "Offer",
      "priceCurrency": "GBP",
      "price": "795",
      "priceValidUntil": "2027-12-31",
      "availability": "https://schema.org/InStock"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Scan to BIM Services London | Revit Modelling from Point Cloud Data"
        description="Professional Scan to BIM services in London. Revit modelling at LOD 200-500 from laser scan data. ISO 19650 compliant. From £795."
        keywords="scan to bim london, revit modelling, bim survey london, point cloud to bim, scan to revit, LOD 300 modelling, ISO 19650"
        canonicalUrl={typeof window !== 'undefined' ? `${window.location.origin}/scan-to-bim-london` : ''}
        structuredData={[
          serviceSchema,
          faqSchema,
          breadcrumbSchema([
            { name: 'Home', url: typeof window !== 'undefined' ? window.location.origin : '' },
            { name: 'Scan to BIM London', url: typeof window !== 'undefined' ? `${window.location.origin}/scan-to-bim-london` : '' }
          ])
        ]}
      />
      <Navbar scrolled={true} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-blue-50 rounded-full mb-6">
              <Boxes className="h-6 w-6 text-blue-600" />
              <span className="text-blue-600 font-semibold">SCAN TO BIM</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Scan to BIM Services London
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transform laser scan data into intelligent, accurate Revit BIM models. Our Scan to BIM service bridges the gap between physical buildings and digital design, providing architects, engineers and contractors with the information they need to deliver projects efficiently.
            </p>
            <div className="mt-8">
              <Link
                to="/pricing"
                className="inline-block px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
              >
                Get a Quote
              </Link>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-16">
            <img
              src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxiaW0lMjBtb2RlbGxpbmclMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHw&ixlib=rb-4.1.0&q=85&w=1400"
              alt="BIM Model of a Commercial Building"
              className="w-full h-[500px] object-cover"
            />
          </div>

          {/* What is Scan to BIM */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-20">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                The Scan to BIM Process
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Scan to BIM is the process of converting 3D laser scan data into a fully intelligent Building Information Model. The workflow begins with a point cloud survey of the existing building, which captures every visible surface in precise 3D. Our BIM modellers then use this data as a reference to construct a Revit model that accurately represents the building's geometry, structure, and services.
                </p>
                <p>
                  Unlike traditional measured surveys that produce static 2D drawings, a BIM model is a living digital representation of the building. Every element — walls, floors, doors, windows, columns, beams, pipes, ducts — is an intelligent object with properties, relationships and classification data. This makes the model far more valuable than drawings alone, enabling clash detection, quantity extraction, energy analysis and lifecycle management.
                </p>
                <p>
                  At Digital Scanning Group, we handle the entire workflow from initial laser scanning through to completed BIM delivery. Our survey team captures the point cloud data on site, and our modelling team produces the Revit model to the Level of Development (LOD) specified for your project. We work to ISO 19650 and PAS 1192 standards, ensuring our outputs meet the information management requirements of public sector and major private sector clients.
                </p>
                <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">Why BIM Matters</h3>
                <p>
                  BIM adoption is now mandated on all centrally procured UK government projects and is increasingly required by private sector clients. For a detailed overview of the requirements, see our guide to <Link to="/blog/bim-level-2-requirements-uk" className="text-blue-600 hover:underline">BIM Level 2 requirements in the UK</Link>. For renovation and refurbishment projects, an accurate as-built BIM model is the foundation for design coordination, clash avoidance and efficient project delivery. The initial investment in Scan to BIM typically pays for itself many times over through reduced design errors, fewer site queries, and better coordination between design disciplines.
                </p>
                <p>
                  Architects increasingly use <Link to="/blog/matterport-for-architects" className="text-blue-600 hover:underline">Matterport alongside BIM workflows</Link> for early-stage feasibility studies and client presentations, before committing to full Scan to BIM modelling. The combination of a visual walkthrough from Matterport and a detailed BIM model from <Link to="/point-cloud-surveys-london" className="text-blue-600 hover:underline">point cloud survey</Link> data provides the most complete digital record of any existing building.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">What We Deliver</h3>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                    <CheckCircle2 className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/pricing"
                className="mt-8 w-full inline-block text-center px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
              >
                Get a Quote
              </Link>
            </div>
          </div>

          {/* LOD Levels */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Levels of Development (LOD)</h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              We model to the Level of Development your project requires, from conceptual design through to verified as-built records.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {lodLevels.map((lod, index) => (
                <div key={index} className="bg-white border-2 border-gray-100 rounded-2xl p-8">
                  <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 font-bold rounded-lg mb-4">{lod.level}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{lod.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">{lod.description}</p>
                  <p className="text-sm text-gray-500"><span className="font-semibold">Typical elements:</span> {lod.typical}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Benefits for Architects and Contractors</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="p-8 bg-blue-50 rounded-2xl text-center">
                    <div className="inline-flex p-4 bg-blue-100 rounded-xl mb-4">
                      <Icon className="h-8 w-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Related Services */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">Related Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link to="/point-cloud-surveys-london" className="p-6 bg-white border-2 border-gray-100 rounded-2xl hover:border-blue-300 transition-colors">
                <Layers className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Point Cloud Surveys</h3>
                <p className="text-gray-600 text-sm">Survey-grade laser scanning to capture accurate as-built spatial data.</p>
              </Link>
              <Link to="/matterport-virtual-tours-london" className="p-6 bg-white border-2 border-gray-100 rounded-2xl hover:border-blue-300 transition-colors">
                <Boxes className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Matterport Virtual Tours</h3>
                <p className="text-gray-600 text-sm">Immersive 3D walkthroughs for property marketing and remote viewing.</p>
              </Link>
              <Link to="/construction" className="p-6 bg-white border-2 border-gray-100 rounded-2xl hover:border-blue-300 transition-colors">
                <Shield className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Construction Scanning</h3>
                <p className="text-gray-600 text-sm">3D reality capture for progress monitoring and verification.</p>
              </Link>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Frequently Asked Questions</h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white border-2 border-gray-100 rounded-2xl p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Need Scan to BIM for Your Project?</h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Whether you need a full building model or a targeted survey of specific areas, we can help. Contact us for a detailed quote and project discussion.
            </p>
            <Link
              to="/pricing"
              className="inline-block px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
            >
              Get a Quote
            </Link>
          </div>
        </div>
      </section>

      <ContactSection />
      <Footer />
    </div>
  );
};

export default ScanToBimPage;
